/**
 * Icon registry — the app's own line icons, drawn on a 16×16 grid.
 *
 * Rendered by components/Icon.svelte. Every shape strokes (or fills) with
 * `currentColor`, so an icon takes the text color of its container and can be
 * restyled from CSS:
 *
 *   .menu-item-danger { color: #dc2626; }           // icon follows the text
 *   .my-button { --icon-color: #16a34a; }           // icon only
 *   :global(.icon-trash) { color: #dc2626; }        // one icon everywhere in a scope
 *
 * Adding an icon: draw it on the 16×16 grid with a 1.5 stroke, round caps and
 * joins, and keep ~1.5px of padding so it optically matches the rest of the set.
 */

export interface IconShape {
  /** SVG path data on the 16×16 grid. */
  d: string;
  /** Fill with currentColor instead of stroking (small dots and solid marks). */
  fill?: boolean;
}

/** A circle as path data, so every shape in the registry is a plain <path>. */
function circle(cx: number, cy: number, r: number): string {
  return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0z`;
}

const ICON_DEFS = {
  // ── Chrome ────────────────────────────────────────────────────────────────
  more: [
    { d: circle(3.5, 8, 1.25), fill: true },
    { d: circle(8, 8, 1.25), fill: true },
    { d: circle(12.5, 8, 1.25), fill: true },
  ],
  close: [{ d: 'M4.5 4.5l7 7M11.5 4.5l-7 7' }],
  plus: [{ d: 'M8 3.5v9M3.5 8h9' }],
  check: [{ d: 'M3.5 8.5l3 3 6-7' }],
  'chevron-left': [{ d: 'M9.75 4L5.75 8l4 4' }],
  'chevron-right': [{ d: 'M6.25 4l4 4-4 4' }],
  'chevron-down': [{ d: 'M4 6.25l4 4 4-4' }],
  search: [{ d: circle(7, 7, 4.5) }, { d: 'M10.5 10.5l3 3' }],
  /** Move up / Move down / Move to (tap alternatives to drag and drop). */
  'arrow-up': [{ d: 'M8 13V3M4 7l4-4 4 4' }],
  'arrow-down': [{ d: 'M8 3v10M4 9l4 4 4-4' }],
  'arrow-right': [{ d: 'M3 8h10M9 4l4 4-4 4' }],

  // ── Task actions ──────────────────────────────────────────────────────────
  /** Duplicate / copy: a sheet stacked on another. */
  duplicate: [
    {
      d: 'M4 5.5h5a1.5 1.5 0 0 1 1.5 1.5v5A1.5 1.5 0 0 1 9 13.5H4A1.5 1.5 0 0 1 2.5 12V7A1.5 1.5 0 0 1 4 5.5z',
    },
    { d: 'M5.5 5.5V4A1.5 1.5 0 0 1 7 2.5h5A1.5 1.5 0 0 1 13.5 4v5a1.5 1.5 0 0 1-1.5 1.5h-1.5' },
  ],
  /**
   * Copy to clipboard (as text): a clipboard with lines of text. Kept distinct
   * from `duplicate`, which creates a new task rather than copying text.
   */
  clipboard: [
    {
      d: 'M5.5 3.25h-.75c-.69 0-1.25.56-1.25 1.25v8.75c0 .69.56 1.25 1.25 1.25h6.5c.69 0 1.25-.56 1.25-1.25V4.5c0-.69-.56-1.25-1.25-1.25h-.75',
    },
    {
      d: 'M6.25 1.75h3.5c.41 0 .75.34.75.75v1c0 .41-.34.75-.75.75h-3.5a.75.75 0 0 1-.75-.75v-1c0-.41.34-.75.75-.75z',
    },
    { d: 'M6 7.75h4M6 10.5h2.5' },
  ],
  /** Move to project: a folder with an arrow going in. */
  'move-to-project': [
    {
      d: 'M1.75 4.25C1.75 3.56 2.31 3 3 3h2.9l1.5 1.75H13c.69 0 1.25.56 1.25 1.25v6c0 .69-.56 1.25-1.25 1.25H3c-.69 0-1.25-.56-1.25-1.25z',
    },
    { d: 'M5.25 9h5.25M8.75 7.25L10.5 9l-1.75 1.75' },
  ],
  /** Move back to the last-used project: a return arrow. */
  'recent-move': [{ d: 'M6 3.5L2.5 7 6 10.5' }, { d: 'M2.5 7h7a4 4 0 0 1 4 4v1.5' }],
  /** Convert to project: a small rocket, launching the task into its own project. */
  convert: [
    { d: 'M13.5 2.5c-3.6 0-6.1 1.8-7.9 5.3l2.6 2.6c3.5-1.8 5.3-4.3 5.3-7.9z' },
    { d: 'M6.3 6.6H3.9L2.5 8.9l2.9.6' },
    { d: 'M9.4 9.7v2.4l-2.3 1.4-.6-2.9' },
    { d: circle(10.4, 5.6, 0.95) },
    { d: 'M4.6 11.4c-1 .5-1.6 2.1-1.6 2.1s1.6-.6 2.1-1.6' },
  ],
  trash: [
    { d: 'M2.5 4.5h11' },
    { d: 'M6 4.5V3a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5v1.5' },
    { d: 'M4 4.5l.62 8.08a1 1 0 0 0 1 .92h4.76a1 1 0 0 0 1-.92L12 4.5' },
    { d: 'M6.75 7v4M9.25 7v4' },
  ],
  /** Won't do: a circle with a slash. */
  'wont-do': [{ d: circle(8, 8, 5.5) }, { d: 'M4.1 11.9l7.8-7.8' }],
  /** Reopen: a counter-clockwise arrow. */
  reopen: [{ d: 'M2.5 8a5.5 5.5 0 1 0 1.6-3.9L2.5 5.75' }, { d: 'M2.5 2.5v3.25h3.25' }],
  /** Now (today's commitments): a target. */
  now: [{ d: circle(8, 8, 5.5) }, { d: circle(8, 8, 1.75), fill: true }],

  // ── Task properties ───────────────────────────────────────────────────────
  priority: [{ d: 'M3.5 14V2.5' }, { d: 'M3.5 2.75h8l-1.75 3 1.75 3h-8' }],
  tag: [
    {
      d: 'M2.5 3.5v3.59c0 .27.1.52.29.7l5.92 5.92a1 1 0 0 0 1.41 0l3.59-3.59a1 1 0 0 0 0-1.41L7.79 2.79a1 1 0 0 0-.7-.29H3.5a1 1 0 0 0-1 1z',
    },
    { d: circle(5.25, 5.25, 0.85), fill: true },
  ],

  /** Open in full page: an arrow leaving a box. */
  open: [
    { d: 'M6.5 3.5h-3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-3' },
    { d: 'M9.5 2.5h4v4M13.5 2.5l-6 6' },
  ],

  // ── Projects ──────────────────────────────────────────────────────────────
  folder: [
    {
      d: 'M1.75 4.25C1.75 3.56 2.31 3 3 3h2.9l1.5 1.75H13c.69 0 1.25.56 1.25 1.25v6c0 .69-.56 1.25-1.25 1.25H3c-.69 0-1.25-.56-1.25-1.25z',
    },
  ],
  edit: [{ d: 'M11 2.75L13.25 5 6 12.25l-3 .75.75-3z' }, { d: 'M9.5 4.25l2.25 2.25' }],
  archive: [
    { d: 'M2.5 3h11a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5z' },
    { d: 'M3 6v6.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6' },
    { d: 'M6.5 8.75h3' },
  ],
  unarchive: [
    { d: 'M2.5 3h11a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5z' },
    { d: 'M3 6v6.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6' },
    { d: 'M8 11.5V8.25M6.5 9.75L8 8.25l1.5 1.5' },
  ],
  /** Backlog: an inbox tray. */
  backlog: [
    { d: 'M2.5 9h3l1 1.75h3l1-1.75h3' },
    {
      d: 'M2.5 9l1.6-4.9a1 1 0 0 1 .95-.6h5.9a1 1 0 0 1 .95.6L13.5 9v3.5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z',
    },
  ],
  calendar: [
    { d: 'M3.5 3.5h9a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z' },
    { d: 'M2.5 6.5h11M5.5 2v2.5M10.5 2v2.5' },
  ],
} satisfies Record<string, IconShape[]>;

export type IconName = keyof typeof ICON_DEFS;

export const ICONS: Record<IconName, IconShape[]> = ICON_DEFS;
